import { useEventConfig } from '@/eventConfig/useEventConfig';
import { useEffect, useState } from 'react';
import { useBmiValues } from './useBmiValues';
import { BmiResult, BmiUtil } from './bmiUtil';
import { FieldType, FormGetFieldValueFn } from '@/form/FormProvider';

interface UseBmiCalculation {
  bmi: BmiResult | undefined;
}

const isProvided = (value: FieldType | undefined): boolean => {
  if (value === undefined || value === null) {
    return false;
  }

  return String(value).trim() !== '';
};

const isFieldProvided = (getFieldValue: FormGetFieldValueFn, fieldName: string): boolean => {
  return isProvided(getFieldValue(fieldName, 'raw'));
};

const isWithinRange = (value: FieldType | undefined, min?: number, max?: number): boolean => {
  const numericValue = Number(value);

  if (Number.isNaN(numericValue)) {
    return false;
  }

  if (min !== undefined && numericValue < min) {
    return false;
  }

  if (max !== undefined && numericValue > max) {
    return false;
  }

  return true;
};

const parseHeightUS = (value: FieldType | undefined): number[] => {
  return String(value)
    ?.split('.')
    .map((part) => parseInt(part, 10))
    .filter((num) => !isNaN(num));
};

export const useBmiCalculation = (getFieldValue: FormGetFieldValueFn): UseBmiCalculation => {
  const { eventConfig } = useEventConfig();
  const { units, height, weight, weightSt, weightLb, heightFt, heightIn } = useBmiValues(getFieldValue);

  const [bmi, setBmi] = useState<BmiResult>();

  const bmiFields = eventConfig?.slides.bmi?.fields ?? [];

  const getRangeValidator = (fieldName: string) => {
    return bmiFields.find(({ name }) => name === fieldName)?.validator?.find(({ type }) => type === 'range');
  };

  const isHeightInRange = () => {
    const range = getRangeValidator('height');
    return isWithinRange(height, range?.min, range?.max);
  };

  const isWeightInRange = () => {
    const range = getRangeValidator('weight');
    return isWithinRange(weight, range?.min, range?.max);
  };

  useEffect(() => {
    let nextBmi: BmiResult | undefined;

    switch (eventConfig?.meta.country) {
      case 'GB': {
        if (units === 'imperial') {
          if (
            isFieldProvided(getFieldValue, 'heightFt') &&
            isFieldProvided(getFieldValue, 'heightIn') &&
            isFieldProvided(getFieldValue, 'weightSt') &&
            isFieldProvided(getFieldValue, 'weightLb')
          ) {
            nextBmi = BmiUtil.calculateBmiImperial(
              Number(heightFt),
              Number(heightIn),
              Number(weightSt),
              Number(weightLb)
            );
          }
        } else {
          if (
            isFieldProvided(getFieldValue, 'height') &&
            isFieldProvided(getFieldValue, 'weight') &&
            isHeightInRange() &&
            isWeightInRange()
          ) {
            nextBmi = BmiUtil.calculateBmiMetric(Number(height), Number(weight));
          }
        }
        break;
      }
      case 'IN': {
        const metricHeight = getFieldValue('height');
        if (
          isFieldProvided(getFieldValue, 'height') &&
          isFieldProvided(getFieldValue, 'weight') &&
          isHeightInRange() &&
          isWeightInRange()
        ) {
          nextBmi = BmiUtil.calculateBmiMetric(Number(metricHeight), Number(weight));
        }
        break;
      }
      case 'US': {
        const parsedHeightUS = parseHeightUS(getFieldValue('height'));
        if (isFieldProvided(getFieldValue, 'height') && isFieldProvided(getFieldValue, 'weight') && isWeightInRange()) {
          const [feet, inch] = parsedHeightUS;
          nextBmi = BmiUtil.calculateBmiAmerican(feet, inch, Number(weight));
        }
        break;
      }
      default: {
        if (
          isFieldProvided(getFieldValue, 'height') &&
          isFieldProvided(getFieldValue, 'weight') &&
          isHeightInRange() &&
          isWeightInRange()
        ) {
          nextBmi = BmiUtil.calculateBmiMetric(Number(height), Number(weight));
        }
        break;
      }
    }

    setBmi(nextBmi);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [units, height, weight, weightSt, weightLb, heightFt, heightIn]);

  return { bmi };
};
